/**
 * Every React Query cache key in the app, in one place.
 *
 * Six surfaces read overlapping data: the explorer and the compare view both
 * read a published method, authoring and the workshop both read a method
 * version, and a plan is generated from either. Keys invented at the call site
 * drift apart, and two keys for one
 * resource means a mutation invalidates one of them and the other surface shows
 * stale data with no way to tell. So they are declared here and nowhere else.
 *
 * Shape convention: `[context, resource, ...discriminators]`, coarse to fine, so
 * `invalidateQueries({ queryKey: keys.methods.all })` reaches every method query
 * without listing them.
 */

export const queryKeys = {
  session: {
    /** `authMe` — identity and effective roles. */
    me: ['auth', 'me'] as const,
    /**
     * `authProviders` — whether this deployment offers a federated sign-in button.
     * Deployment configuration, not user data, so it is cached for the session and
     * never invalidated by a mutation.
     */
    providers: ['auth', 'providers'] as const,
  },

  methods: {
    all: ['methods'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['methods', 'list', filter] as const,
    detail: (methodVersionId: string) => ['methods', 'detail', methodVersionId] as const,
    graph: (methodVersionId: string) => ['methods', 'graph', methodVersionId] as const,
    validation: (methodVersionId: string) => ['methods', 'validation', methodVersionId] as const,
    locks: (methodVersionId: string) => ['methods', 'locks', methodVersionId] as const,
  },

  published: {
    all: ['published'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['published', 'list', filter] as const,
    detail: (publishedMethodId: string) => ['published', 'detail', publishedMethodId] as const,
    /** Ordered pair: comparing A→B is not the same query as B→A. */
    compare: (baselineId: string, candidateId: string) =>
      ['published', 'compare', baselineId, candidateId] as const,
  },

  catalog: {
    all: ['catalog'] as const,
    kind: (kind: string) => ['catalog', kind] as const,
    list: (kind: string, filter: Readonly<Record<string, unknown>>) =>
      ['catalog', kind, 'list', filter] as const,
    detail: (kind: string, id: string) => ['catalog', kind, 'detail', id] as const,
    search: (query: string) => ['catalog', 'search', query] as const,
    /**
     * The promotion queue: client entries a facilitator has argued belong to everybody.
     *
     * Under `catalog` and not under `workshops`, because both writes that change it are
     * catalogue writes — accepting one *is* a platform catalogue insert — and an author
     * working the queue is reading the platform's own backlog, not one room's. The two
     * keys are the pair the `people` prefix above exists for, for the same reason: a
     * decision changes both the queue and the entry list it just added a row to, and
     * `promotionsAll` is what a proposal invalidates without dropping every catalogue page.
     */
    promotionsAll: ['catalog', 'promotions'] as const,
    promotions: (filter: Readonly<Record<string, unknown>>) =>
      ['catalog', 'promotions', 'list', filter] as const,
  },

  deliverables: {
    all: ['deliverables'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['deliverables', 'list', filter] as const,
    detail: (deliverableId: string) => ['deliverables', 'detail', deliverableId] as const,
    versions: (deliverableId: string) => ['deliverables', 'versions', deliverableId] as const,
    version: (deliverableId: string, versionId: string) =>
      ['deliverables', 'versions', deliverableId, versionId] as const,
  },

  workshops: {
    all: ['workshops'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['workshops', 'list', filter] as const,
    detail: (sessionId: string) => ['workshops', 'detail', sessionId] as const,
    /**
     * What a client brought into one adoption. Under the `workshops` prefix on purpose:
     * the entries live in the session's scope and every catalogue write is also a change
     * to what that session can bind, so the existing session invalidation must reach both
     * without each command hook naming two keys.
     */
    catalogue: (sessionId: string, filter: Readonly<Record<string, unknown>>) =>
      ['workshops', 'catalogue', sessionId, filter] as const,
  },

  plans: {
    all: ['plans'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['plans', 'list', filter] as const,
    detail: (planId: string) => ['plans', 'detail', planId] as const,
    /**
     * Templates are code, not rows: they change on deploy, never in response to
     * anything a user does. One key with no discriminator, and no mutation
     * invalidates it — a list key with a filter would suggest it can go stale.
     */
    templates: ['plans', 'templates'] as const,
  },

  attachments: {
    all: ['attachments'] as const,
    list: (subjectKind: string, subjectId: string) =>
      ['attachments', subjectKind, subjectId] as const,
  },

  assist: {
    all: ['assist'] as const,
    invocations: (filter: Readonly<Record<string, unknown>>) =>
      ['assist', 'invocations', filter] as const,
  },

  admin: {
    all: ['admin'] as const,
    settings: ['admin', 'settings'] as const,
    /** The Bedrock credential is never cached: only its masked hint is readable. */
    bedrockConnection: ['admin', 'bedrock', 'connection'] as const,
    /**
     * Accounts and role assignments are two keys under one `people` prefix, because
     * one write moves both: granting a role to an address that already has an
     * account writes an assignment row *and* changes that account's role. The
     * prefix is what lets that mutation invalidate the pair without invalidating
     * the settings record it did not touch.
     */
    people: ['admin', 'people'] as const,
    accounts: (filter: Readonly<Record<string, unknown>>) =>
      ['admin', 'people', 'accounts', filter] as const,
    roleAssignments: (includeApplied: boolean) =>
      ['admin', 'people', 'role-assignments', includeApplied] as const,
    /**
     * What an administrative purge of a method version would destroy.
     *
     * Under `admin` rather than under `methods`, deliberately: it is an ADMIN-only read that
     * only an administrator's dialog ever makes, and a key under `methods` would be dropped
     * by every authoring command's invalidation — refetching a destruction preview after each
     * unrelated edit, for a dialog that is almost never open. Nothing invalidates this key;
     * the query is `staleTime: 0` because the only moment it is asked is the moment it is read.
     */
    methodPurge: (methodVersionId: string) =>
      ['admin', 'purge', 'method', methodVersionId] as const,
  },

  audit: {
    all: ['audit'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['audit', 'list', filter] as const,
  },
} as const;
